import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useSetAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { type SocialApp } from "shared/apiClient";

import { isAuthenticatedVar } from "@/variables/globalVar";
import {
  getAccountQueryKey,
  useGetAuthSocialList,
  useLogin,
  useSignUp,
} from "shared/apiHooks";
import { getText, interpolate } from "shared/lib";

import { useCaptureEvent, CaptureEventType } from "@/hooks";

import { IconSpinner } from "@/components/icons";
import {
  Button,
  Checkbox,
  Input,
  Separator,
  SocialLoginButtons,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "shared/components";
import { TermsOfService } from "./TermsOfService";

export function SignUp() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { captureEvent } = useCaptureEvent();
  const setAuthenticatedVar = useSetAtom(isAuthenticatedVar);

  const [showEmailConfirm, setShowEmailConfirm] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>("");

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [socialApps, setSocialApps] = useState<SocialApp[]>([]);

  const [consentChecked, setConsentChecked] = useState(false);

  const {
    data: authSocialListResponse,
    error: authSocialListError,
    isLoading: isLoadingAuthSocialList,
  } = useGetAuthSocialList();

  useEffect(() => {
    if (authSocialListError) {
      console.error(
        "<SignUp> useGetAuthSocialList() onError()",
        authSocialListError,
      );
      setErrorMessage(getText("Failed to load social login options."));
    }
  }, [authSocialListError]);

  useEffect(() => {
    if (authSocialListResponse) {
      const results = authSocialListResponse.data?.results || [];
      const transformedResults = results.map((app) => ({
        ...app,
        login_url: app.login_url ?? "", // Fallback to an empty string if login_url is undefined
      }));
      setSocialApps(transformedResults);
    }
  }, [authSocialListResponse]);

  const { isPending: isLoginPending, mutate: mutateLogin } = useLogin({
    onError: (error) => {
      console.error("<SignUp> useLogin() onError()", error);
      const response = error.response;
      if (response?.data && Array.isArray(response.data["non_field_errors"])) {
        setErrorMessage(response.data["non_field_errors"][0]);
      } else {
        setErrorMessage(
          getText(
            "There was an unexpected error logging you in. Please refresh your browser and try again, or contact support for assistance.",
          ),
        );
      }
    },
    onSuccess: (response) => {
      console.log("<SignUp> useLogin() onSuccess()", response);
      if (response.data) {
        queryClient.setQueryData(getAccountQueryKey(), response);

        captureEvent(CaptureEventType.USER_LOGGED_IN, {
          email: response.data.email,
        });

        setAuthenticatedVar(true);
      }
    },
  });

  const { isPending: isSignupPending, mutate: mutateSignup } = useSignUp({
    onError: (error) => {
      console.error("<SignUp> useSignUp() onError()", error);
      const response = error.response;
      if (response?.data && Array.isArray(response.data["non_field_errors"])) {
        setErrorMessage(response.data["non_field_errors"][0]);
      } else if (response?.data && typeof response?.data === "object") {
        const keys = Object.keys(response.data);
        if (keys.length > 0) {
          const errors = keys.reduce((memo, key) => {
            if (Array.isArray(response.data?.[key])) {
              memo.push(
                <Fragment key={key}>
                  {response.data[key][0]}
                  <br />
                </Fragment>,
              );
            }

            return memo;
          }, [] as Array<ReactNode>);
          setErrorMessage(errors);
        }
      } else {
        setErrorMessage(
          getText(
            "There was an unexpected error signing you up. Please refresh your browser and try again, or contact support for assistance.",
          ),
        );
      }
    },
    onSuccess: (response) => {
      console.log("<SignUp> useSignUp() onSuccess()", response);

      setErrorMessage("");
      setShowEmailConfirm(true);

      const email = emailRef.current?.value || "";
      const password = passwordRef.current?.value || "";

      captureEvent(CaptureEventType.USER_SIGNED_UP, {
        email: response.data.email,
      });

      mutateLogin({ body: { email, password } });
    },
  });

  const handleClickConsentCheckbox = (checked: boolean) => {
    setConsentChecked(checked);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const email = emailRef.current?.value || "";
    const password = passwordRef.current?.value || "";

    setErrorMessage("");
    mutateSignup({
      body: {
        email,
        password1: password,
        password2: password,
        contact_consent: consentChecked,
        tc_read: true,
      },
    });
  };

  return (
    <Tabs defaultValue="signup" className="w-full">
      <TabsList className="border-blueBorder grid w-full grid-cols-2 rounded-none border-b">
        <button
          type="button"
          onClick={() => {
            navigate("/login");
          }}
          className="text-bodymd inline-flex items-center justify-center rounded-none border-b-0 py-3 font-semibold whitespace-nowrap transition-all hover:text-black disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-transparent"
        >
          {getText("Log in")}
        </button>
        <TabsTrigger
          value="signup"
          className="rounded-none border-b-0 data-[state=active]:bg-transparent"
        >
          {getText("Sign up")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="signup" className="flex flex-col gap-6 px-0 py-6">
        {!isLoadingAuthSocialList ? (
          <>
            <SocialLoginButtons
              socialApps={socialApps}
              disabled={isLoginPending || isSignupPending}
            />

            <div className="flex items-center gap-2">
              <Separator className="w-auto grow" />
              <span className="text-bodyxs text-textSubdued">
                {getText("or sign up with email")}
              </span>
              <Separator className="w-auto grow" />
            </div>
          </>
        ) : null}

        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label
                className="text-headingsm text-textPrimary"
                htmlFor="email"
              >
                {getText("Email address")}
              </label>
              <Input
                className="border-interactiveBorder text-bodymd rounded-lg p-4"
                id="email"
                ref={emailRef}
                type="email"
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                disabled={isLoginPending || isSignupPending}
                required
              />
              <p className="text-textSubdued text-bodyxs">
                {getText(
                  "Your email address is used to send you a copy of your enquiry's result.",
                )}
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <label
                className="text-headingsm text-textPrimary"
                htmlFor="password"
              >
                {getText("Password")}
              </label>
              <Input
                className="border-interactiveBorder text-bodymd rounded-lg p-4"
                id="password"
                type="password"
                ref={passwordRef}
                autoCapitalize="none"
                autoComplete="password"
                autoCorrect="off"
                disabled={isLoginPending || isSignupPending}
                title={getText("Password must be atleast 8 characters")}
                pattern={"^.{8,}$"}
                required
              />
              <p className="text-textSubdued text-bodyxs">
                {getText(
                  "Must be at least 8 characters long and contain both letters and numbers.",
                )}
              </p>
            </div>

            <div className="flex flex-col gap-6">
              <div className="inline-flex">
                <div>
                  <Checkbox
                    role="checkbox"
                    className="border-interactiveBorder rounded-sm"
                    id="feedback-consent"
                    name="feedback-consent"
                    onCheckedChange={handleClickConsentCheckbox}
                  />
                </div>
                <div className="ml-2">
                  <label
                    className="text-textPrimary text-bodymd"
                    htmlFor="feedback-consent"
                  >
                    {interpolate(
                      "Contact me by email for feedback about this service {{optionalLabel}}",
                      {
                        optionalLabel: (
                          <span className="text-textSubdued">
                            ({getText("optional")})
                          </span>
                        ),
                      },
                    )}
                  </label>
                </div>
              </div>

              <TermsOfService />

              <Button
                className="!text-headingmd"
                disabled={isLoginPending || isSignupPending}
                variant="default"
              >
                {getText("Sign up")}
                {isLoginPending || isSignupPending ? (
                  <IconSpinner className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
              </Button>
            </div>
            <div
              hidden={!showEmailConfirm}
              className="mt-6 rounded-sm border border-black bg-blue-300 p-3"
            >
              {getText(
                "Your account has been created. Please login to continue.",
              )}
            </div>
            <div
              hidden={errorMessage === ""}
              className="border-destructiveBorder bg-destructiveBgDefault text-destructiveText mt-6 rounded-sm border p-3"
            >
              {errorMessage}
            </div>
          </div>
        </form>
      </TabsContent>
    </Tabs>
  );
}
